"use client";

import * as React from "react";
import { ExternalLink, RefreshCw, Trash2, CopyCheck } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../../primitives/context-menu";

export interface RowMenuActions {
  onOpenRow?: () => void;
  onRetryRow?: () => void;
  onDuplicateRow?: () => void;
  onRemoveRow?: () => void;
  /** Override the label for the "Open row" item (e.g. "Open location"). */
  openRowLabel?: string;
  /** Override the label for the "Retry row" item. */
  retryRowLabel?: string;
  /** Override the label for the "Duplicate row" item. */
  duplicateRowLabel?: string;
  /** Override the label for the "Remove row" item (e.g. "Delete location"). */
  removeRowLabel?: string;
}

export interface RowMenuProps extends RowMenuActions {
  children: React.ReactNode;
  disabled?: boolean;
  /** Force the menu open for stories. */
  defaultOpen?: boolean;
}

/**
 * Right-click context menu for a grid row.
 */
export function RowMenu({
  children,
  disabled,
  onOpenRow,
  onRetryRow,
  onDuplicateRow,
  onRemoveRow,
  openRowLabel = "Open row",
  retryRowLabel = "Retry row",
  duplicateRowLabel = "Duplicate row",
  removeRowLabel = "Remove row",
  defaultOpen,
}: RowMenuProps) {
  if (disabled) return <>{children}</>;

  return (
    <ContextMenu {...(defaultOpen ? { modal: false } : {})}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        {onOpenRow && (
          <ContextMenuItem onSelect={() => onOpenRow()}>
            <ExternalLink className="size-3.5" /> {openRowLabel}
          </ContextMenuItem>
        )}
        {onRetryRow && (
          <ContextMenuItem onSelect={() => onRetryRow()}>
            <RefreshCw className="size-3.5" /> {retryRowLabel}
          </ContextMenuItem>
        )}
        {onDuplicateRow && (
          <ContextMenuItem onSelect={() => onDuplicateRow()}>
            <CopyCheck className="size-3.5" /> {duplicateRowLabel}
          </ContextMenuItem>
        )}
        {onRemoveRow && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem variant="destructive" onSelect={() => onRemoveRow()}>
              <Trash2 className="size-3.5" /> {removeRowLabel}
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
